import { routeLoaders } from '../routeLoaders';
import CurrentRiskBadge from '../components/CurrentRiskBadge';
import { cellCode } from '../utils/riskUtils';
import { plural } from '../utils/plural';
import AssetRegisterTransfer from '../components/AssetRegisterTransfer';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../components/ConfirmDialog';
import { incidentRisk } from '../utils/incidentRisk';
import { friendlyLabel } from '../utils/labels';
import FacilityDocuments from '../components/FacilityDocuments';
import FacilityRiskAssessment from '../components/FacilityRiskAssessment';
import FacilityRiskProfile from '../components/FacilityRiskProfile';
import RiskBadge from '../components/RiskBadge';
import { highestRisk, isOpenAction } from '../utils/riskUtils';
import { Task, LabIncidentListItem } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  MapPin,
  Package,
  ClipboardList,
  AlertTriangle,
  Settings,
  ArrowLeft,
  Plus,
  TrendingUp,
  Clock,
  FileText,
  FlaskConical,
  ShieldCheck,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';



import FacilityLocations from '../components/FacilityLocations';
import { getLocationPath } from '../utils/locationPaths';
import QuickAddSpotlight from '../components/Forms/QuickAddSpotlight';
import CloneAssetModal from '../components/Forms/CloneAssetModal';
import AddTaskModal from '../components/Forms/AddTaskModal';
import FacilitySettingsModal, { FacilitySettings } from '../components/Forms/FacilitySettingsModal';
import AssetDetailsModal from '../components/Forms/AssetDetailsModal';
import './FacilityDetail.css';
import {
  facilitiesService,
  assetsService,
  locationsService,
} from '../services';
import { Asset, AssetTypeDef, Location, Client, Facility, Inspection, User as UserType } from '../types';
import {
  riskLabel,
} from '../utils/riskUtils';
import { formatShortDate } from '../utils/dateUtils';

const FacilityLab = lazy(() => import('../components/FacilityLab'));
const FacilityReports = lazy(() => import('../components/FacilityReports'));
const WrmpPanel = lazy(() => import('../components/WrmpPanel'));

type DepthAsset = {
  asset: Asset;
  depth: number;
  hasChildren: boolean;
};

const buildDepthOrderedList = (
  assetList: Asset[],
  collapsedMap: Record<string, boolean>,
): DepthAsset[] => {
  if (assetList.length === 0) {
    return [];
  }

  const assetById = new Map(assetList.map((asset) => [asset.id, asset]));
  const childrenMap = new Map<string, Asset[]>();

  assetList.forEach((asset) => {
    const parentId = asset.parent_asset_id;
    if (!parentId || !assetById.has(parentId)) {
      return;
    }
    const siblings = childrenMap.get(parentId) || [];
    siblings.push(asset);
    childrenMap.set(parentId, siblings);
  });

  const roots = assetList.filter(
    (asset) => !asset.parent_asset_id || !assetById.has(asset.parent_asset_id),
  );

  const ordered: DepthAsset[] = [];
  const visited = new Set<string>();

  const skipSubtree = (asset: Asset) => {
    if (visited.has(asset.id)) {
      return;
    }
    visited.add(asset.id);
    const children = childrenMap.get(asset.id) || [];
    children.forEach((child) => skipSubtree(child));
  };

  const visit = (asset: Asset, depth: number) => {
    if (visited.has(asset.id)) {
      return;
    }
    visited.add(asset.id);
    const children = childrenMap.get(asset.id) || [];
    ordered.push({ asset, depth, hasChildren: children.length > 0 });
    if (collapsedMap[asset.id]) {
      children.forEach((child) => skipSubtree(child));
      return;
    }
    children.forEach((child) => visit(child, depth + 1));
  };

  roots.forEach((root) => visit(root, 0));

  assetList.forEach((asset) => {
    if (!visited.has(asset.id)) {
      visit(asset, asset.parent_asset_id ? 1 : 0);
    }
  });

  return ordered;
};

const FacilityDetail: React.FC = () => {
  usePageTitle('Facility');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const completedInspection = (location.state as { completedInspection?: { id: string; actions: number } } | null)?.completedInspection;
  const [labIncidents, setLabIncidents] = useState<LabIncidentListItem[] | null | undefined>(null);
  const [activeTab, setActiveTab] = useState<'assets' | 'locations' | 'inspections' | 'reports' | 'lab' | 'documents' | 'wrmp' | 'risk'>('assets');
  const [spotlightMode, setSpotlightMode] = useState<'location' | 'asset' | null>(null);
  const [selectedParentAsset, setSelectedParentAsset] = useState<Asset | null>(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAssetDetailsModal, setShowAssetDetailsModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [assetToClone, setAssetToClone] = useState<Asset | null>(null);
  const { currentUser } = useAuth();
  const [inspectionFilter, setInspectionFilter] = useState('all');
  const [incidentCount, setIncidentCount] = useState(0);
  const [inspectionChecksDue,setInspectionChecksDue] = useState(0);
  const [inspectors,setInspectors] = useState<UserType[]>([]);
  const [selectedCheckId,setSelectedCheckId] = useState<string>();
  const [libraryCount, setLibraryCount] = useState(0);
  useEffect(() => { void routeLoaders.InspectionMode().catch(() => {}); }, []);

  const { confirm, dialog } = useConfirmation();
  const [riskTasks, setRiskTasks] = useState<Task[]>([]);
  const [riskError, setRiskError] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [registryTypes, setRegistryTypes] = useState<AssetTypeDef[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationCollapsed, setLocationCollapsed] = useState<Record<string, boolean>>({});
  const [assetQuery, setAssetQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [manager, setManager] = useState<UserType | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [facilityInspections, setFacilityInspections] = useState<Inspection[]>([]);
  const [inspectionTaskCounts, setInspectionTaskCounts] = useState<Record<string, { total: number; complete: number }>>({});
  const [draggedAssetId, setDraggedAssetId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const depthOrderedAssets = useMemo(
    () => buildDepthOrderedList(assets, collapsed),
    [assets, collapsed],
  );
  const [labRisk, setLabRisk] = useState<ReturnType<typeof incidentRisk>>(null);
  const facilityAggregatedRisk = useMemo(
    () => highestRisk([
      ...(labRisk ? [labRisk] : []),
      ...assets.map(a => ({ likelihood: a.current_likelihood ?? a.risk_likelihood, consequence: a.current_consequence ?? a.risk_significance })),
      ...riskTasks.filter(isOpenAction).map(t => ({ likelihood: t.risk_likelihood, consequence: t.risk_consequence })),
    ]),
    [assets, riskTasks, labRisk],
  );

  useEffect(() => {
    const loadFacility = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          throw new Error('Missing facility id');
        }

        const page = await facilitiesService.getPage(id);
        const {facility: facilityData, assets: facilityAssets, locations: facilityLocations, asset_types: types} = page;
        setRiskTasks(page.tasks);
        setRiskError(false);
        setLibraryCount(page.check_summary.not_done + page.check_summary.overdue);
        setLabIncidents(page.open_incidents);
        setLabRisk(incidentRisk(page.open_incidents));
        setIncidentCount(page.open_incidents.length);
        setInspectors(page.assignable_users);
        setManager(page.assignable_users.find(user => user.id === facilityData.manager_id) ?? null);
        setClient(page.client);
        setInspectionChecksDue(page.checks_due);
        setFacilityInspections(page.inspections);
        setInspectionTaskCounts(Object.fromEntries(page.inspections.map(insp => [insp.id, {total: insp.task_count || 0, complete: insp.complete_task_count || 0}])));

        setFacility(facilityData);
        setAssets(facilityAssets);
        setLocationCollapsed(Object.fromEntries(facilityLocations.map(node => [
          node.id, facilityAssets.length > 30 && !!node.parent_location_id &&
            facilityLocations.some(parent => parent.id === node.parent_location_id),
        ])));
        setAssetQuery('');
        setLocations(facilityLocations);
        setRegistryTypes(types);

      } catch (loadError) {
        console.error('Failed to load facility detail', loadError);
        setError(loadError instanceof Error ? loadError.message : 'Failed to load facility');
      } finally {
        setLoading(false);
      }
    };

    loadFacility();
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'risk' || tabParam === 'assets' || tabParam === 'locations' || tabParam === 'inspections' || tabParam === 'reports' || tabParam === 'lab' || tabParam === 'wrmp' || tabParam === 'documents') {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  const stats = useMemo(() => {
    const openInspections = facilityInspections.filter(
      (i) => i.status !== 'complete' && i.status !== 'cancelled'
    ).length;
    const openInspTrend = openInspections > 0 ? 'down' : 'neutral';

    const totalCheckpoints = inspectionChecksDue;

    return [
      { label: 'Total assets', value: assets.length.toString(), change: '', trend: 'neutral', color: '#0077be', tab: 'assets' as const },
      { label: 'Checks due', value: (totalCheckpoints + libraryCount).toString(), change: '', trend: 'neutral', color: '#4fc3f7', tab: 'risk' as const },
      { label: 'Inspections in progress', value: openInspections.toString(), change: '', trend: openInspTrend, color: '#ff9800', tab: 'inspections' as const },
    ];
  }, [assets, facilityAggregatedRisk, facilityInspections, libraryCount, inspectionChecksDue]);

  const refreshAssets = async () => {
    if (!id) {
      return;
    }
    try {
      const [latestAssets, latestLocations, latestFacility] = await Promise.all([facilitiesService.getFacilityAssets(id), locationsService.getFacilityLocations(id), facilitiesService.getFacilityById(id)]);
      setFacility(latestFacility);
      setLocations(latestLocations);
      setAssets(latestAssets);
    } catch (refreshError) {
      console.error('Failed to refresh assets', refreshError);
    }
  };

  const handleAddAsset = async (newAsset: Asset) => {
    setAssets((prev) => [...prev, newAsset]);
    setSelectedParentAsset(null);
    await refreshAssets();
  };

  const handleAddRootAsset = () => {
    setSelectedParentAsset(null);
    setSpotlightMode('asset');
  };

  const handleAddSubAsset = (parentAsset: Asset) => {
    setSelectedParentAsset(parentAsset);
    setSpotlightMode('asset');
  };

  const openCloneModal = (asset: Asset) => {
    setAssetToClone(asset);
    setShowCloneModal(true);
  };

  const openAssetDetailsModal = (asset: Asset) => {
    setSelectedAsset(asset);
    setShowAssetDetailsModal(true);
  };

  const closeCloneModal = () => {
    setShowCloneModal(false);
    setAssetToClone(null);
  };

  const LOCATION_TYPES = new Set(['building', 'floor', 'room']);

  const locationAssets = assets.filter((a) => LOCATION_TYPES.has(a.type));

  const toggleCollapsed = (assetId: string, event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setCollapsed((prev) => ({
      ...prev,
      [assetId]: !prev[assetId],
    }));
  };

  const handleDeleteAsset = async (asset: Asset) => {
    if (!await confirm(`Delete ${asset.name} and all sub-assets?`)) {
      return;
    }
    try {
      await assetsService.deleteAsset(asset.id);
      await refreshAssets();
    } catch (deleteError) {
      console.error('Failed to delete asset', deleteError);
      setError('Failed to delete asset');
    }
  };

  const handleDragStart = (e: React.DragEvent, assetId: string) => {
    setDraggedAssetId(assetId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, targetId: string | null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(targetId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if leaving to outside the drop target (not entering a child)
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setDropTargetId(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetId: string | null) => {
    e.preventDefault();
    const dragged = draggedAssetId;
    setDraggedAssetId(null);
    setDropTargetId(null);
    if (!dragged || dragged === targetId) return;

    // Prevent dropping onto own descendant
    const isDescendant = (candidateId: string, ancestorId: string): boolean => {
      const candidate = assets.find((a) => a.id === candidateId);
      if (!candidate?.parent_asset_id) return false;
      if (candidate.parent_asset_id === ancestorId) return true;
      return isDescendant(candidate.parent_asset_id, ancestorId);
    };
    if (targetId && isDescendant(targetId, dragged)) return;

    try {
      await assetsService.updateAsset(dragged, {
        parent_asset_id: targetId ?? undefined,
      });
      await refreshAssets();
    } catch (err) {
      console.error('Failed to reparent asset', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: { class: 'badge badge-success', label: 'Active' },
      maintenance: { class: 'badge badge-warning', label: 'Maintenance' },
      inactive: { class: 'badge badge-error', label: 'Inactive' }
    };
    return statusMap[status as keyof typeof statusMap] || statusMap.active;
  };



  const getAssetIcon = (type: string) => {
    if (type === 'building') return '🏢';
    if (type === 'floor') return '🧱';
    if (type === 'room') return '🚪';
    return '';
  };



  const searchQuery = assetQuery.trim().toLocaleLowerCase();
  const matchingAssets = assets.filter(asset => {
    const node = locations.find(node => node.id === asset.location_id);
    return [asset.name, registryTypes.find(type => type.id === asset.asset_type_id)?.name || friendlyLabel(asset.type),
      node ? getLocationPath(node, locations) : 'No location']
      .some(value => value?.toLocaleLowerCase().includes(searchQuery));
  });

  const renderEquipmentRows = (asset: Asset, depth: number = 0): React.ReactNode => {
    const children = matchingAssets.filter(
      (a) => a.parent_asset_id === asset.id && a.location_id === asset.location_id
    );
    const hasChildren = children.length > 0;
    const isCollapsed = !searchQuery && collapsed[asset.id];

    const isDragging = draggedAssetId === asset.id;
    const isDropTarget = dropTargetId === asset.id;

    return (
      <React.Fragment key={asset.id}>
        <div
          className={`asset-row${isDragging ? ' asset-row--dragging' : ''}${isDropTarget ? ' asset-row--drop-target' : ''}`}
          style={{ '--asset-depth': depth } as React.CSSProperties}
          draggable
          onDragStart={(e) => handleDragStart(e, asset.id)}
          onDragEnd={() => { setDraggedAssetId(null); setDropTargetId(null); }}
          onDragOver={(e) => handleDragOver(e, asset.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => { e.stopPropagation(); handleDrop(e, asset.id); }}
        >
          {hasChildren && <div className="asset-row__indent-track">
            <button
              type="button"
              className="asset-row__chevron"
              disabled={!!searchQuery}
              onClick={(e) => toggleCollapsed(asset.id, e)}
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>}
          <div className="asset-row__body">
            <span className="asset-row__name">{asset.name}</span>
            {asset.type && <span className="asset-type-tag">{registryTypes.find(row => row.id === asset.asset_type_id)?.name || friendlyLabel(asset.type)}</span>}
            <span title={`Inherent ${cellCode(asset.risk_likelihood || 0, asset.risk_significance || 0)} · current ${cellCode(asset.current_likelihood ?? asset.risk_likelihood ?? 0, asset.current_consequence ?? asset.risk_significance ?? 0)} (${asset.current_reason === 'Under control' ? 'checks up to date' : asset.current_reason || (asset.risk_source === 'action' ? `Open action: ${asset.risk_task_title}` : asset.risk_source === 'incident' ? 'Open lab incident' : 'inherent risk')})`}><RiskBadge likelihood={asset.current_likelihood ?? asset.risk_likelihood} consequence={asset.current_consequence ?? asset.risk_significance} /></span>
            <span className={getStatusBadge('active').class}>{getStatusBadge('active').label}</span>
            {hasChildren && isCollapsed && (
              <span className="asset-row__child-count">{children.length} sub-asset{children.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="asset-row__actions">
            <button className="btn btn-ghost btn-sm" onClick={() => handleAddSubAsset(asset)}>
              <Plus size={13} /> Sub-asset
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => openAssetDetailsModal(asset)}>View</button>
            <button className="btn btn-ghost btn-sm" onClick={() => openCloneModal(asset)}>Clone</button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteAsset(asset)}>Delete</button>
          </div>
        </div>
        {!isCollapsed && children.map((child) => renderEquipmentRows(child, depth + 1))}
      </React.Fragment>
    );
  };

  const renderAssetsTab = () => {
    type LocationBranch = { node: Location; children: LocationBranch[]; direct: Asset[]; subtree: Asset[] };
    const branches = new Map<string, LocationBranch>(locations.map(node => [
      node.id, { node, children: [], direct: [], subtree: [] },
    ]));
    const roots: LocationBranch[] = [];
    const unassigned: Asset[] = [];
    branches.forEach(branch => {
      const parent = branches.get(branch.node.parent_location_id || '');
      if (parent) parent.children.push(branch);
      else roots.push(branch);
    });
    matchingAssets.forEach(asset => {
      const branch = branches.get(asset.location_id || '');
      if (branch) branch.direct.push(asset);
      else unassigned.push(asset);
    });
    /**
     * Collect matching assets and sort children for one location subtree.
     *
     * Args:
     *   branch: Location whose descendants should be summarized.
     *
     * Returns:
     *   Matching assets in this branch and all its descendants.
     */
    const collectSubtree = (branch: LocationBranch): Asset[] => {
      branch.children.sort((a, b) => a.node.name.localeCompare(b.node.name));
      branch.subtree = [...branch.direct, ...branch.children.flatMap(collectSubtree)];
      return branch.subtree;
    };
    roots.sort((a, b) => a.node.name.localeCompare(b.node.name)).forEach(collectSubtree);
    /**
     * Render each asset once, keeping same-location asset children nested.
     *
     * Args:
     *   group: Matching assets assigned directly to one location.
     *
     * Returns:
     *   Root asset rows, including their nested children.
     */
    const renderGroup = (group: Asset[]) => group
      .filter(asset => !group.some(parent => parent.id === asset.parent_asset_id))
      .map(asset => renderEquipmentRows(asset));
    /**
     * Render a nonempty location with its subtree summary and toggle.
     *
     * Args:
     *   branch: Location branch with matching assets and children.
     *   depth: Location nesting depth.
     *
     * Returns:
     *   A collapsible section, or null for an empty subtree.
     */
    const renderLocation = (branch: LocationBranch, depth = 0): React.ReactNode => {
      if (!branch.subtree.length) return null;
      const { node } = branch;
      const open = !!searchQuery || !locationCollapsed[node.id];
      const risk = highestRisk(branch.subtree.map(asset => ({
        likelihood: asset.current_likelihood ?? asset.risk_likelihood, consequence: asset.current_consequence ?? asset.risk_significance,
      })));
      return <section className={`location-section location-section--depth-${depth}`} key={node.id}>
        <h4 className="registry-location-heading">
          <button type="button" className="location-section__header registry-location-toggle"
            aria-expanded={open}
            onClick={() => { if (!searchQuery) setLocationCollapsed(previous => ({ ...previous, [node.id]: open })); }}>
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>{node.name}</span>
            <span className="location-section__desc">{plural(branch.subtree.length, 'asset')}</span>
            <RiskBadge likelihood={risk?.likelihood} consequence={risk?.significance} />
          </button>
        </h4>
        {open && <div className="location-section__content">
          {renderGroup(branch.direct)}
          {branch.children.map(child => renderLocation(child, depth + 1))}
        </div>}
      </section>;
    };

    return (
      <div className="assets-content">
        <div className="assets-header">
          <h3>Asset Registry</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" disabled={!!searchQuery}
              onClick={() => setLocationCollapsed({})}>Expand all</button>
            <button className="btn btn-secondary" disabled={!!searchQuery}
              onClick={() => setLocationCollapsed(Object.fromEntries(locations.map(node => [node.id, true])))}>Collapse all</button>
            <button className="btn btn-secondary" onClick={() => navigate(`/facilities/${id}/inspection`)}>
              <ClipboardList size={18} />
              Run inspection
            </button>
            <button className="btn btn-secondary" onClick={() => setSpotlightMode('location')}>
              <MapPin size={16} />
              Add Location
            </button>
            {id && <AssetRegisterTransfer facilityId={id} canEdit={currentUser?.role === 'consultant' || currentUser?.role === 'h2okay_admin'} onImported={refreshAssets} />}
            <button className="btn btn-primary" onClick={() => setSpotlightMode('asset')}>
              <Plus size={16} />
              Add Asset
            </button>
          </div>
        </div>

        {assets.length === 0 && (
          <div className="empty-state">
            <Package size={48} />
            <h4>Start Building Your Asset Registry</h4>
            <p>Add your first asset to begin tracking equipment and setting up inspection schedules.</p>
            <button className="btn btn-primary" onClick={handleAddRootAsset}>
              <Plus size={18} />
              Add Your First Asset
            </button>
          </div>
        )}

        <div
          className={`root-drop-zone${!draggedAssetId ? ' root-drop-zone--hidden' : ''}${dropTargetId === '__root__' ? ' root-drop-zone--active' : ''}`}
          onDragOver={(e) => handleDragOver(e, '__root__')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, null)}
        >
          Drop here to move to root level
        </div>

        <label className="registry-search">
          Search assets
          <input type="search" placeholder="Search assets" value={assetQuery}
            onChange={event => setAssetQuery(event.target.value)} />
        </label>
        {roots.map(branch => renderLocation(branch))}
        {unassigned.length > 0 && <section className="location-section">
          <div className="location-section__header location-section__header--unassigned"><h4>No location</h4></div>
          <div className="location-section__content">{renderGroup(unassigned)}</div>
        </section>}
        {searchQuery && matchingAssets.length === 0 && <p>No matching assets.</p>}
      </div>
    );
  };

  const renderInspectionsTab = () => {
    const STATUS_META: Record<string, { label: string; accentColor: string; statusClass: string }> = {
      not_started: { label: 'Not started', accentColor: '#9e9e9e', statusClass: 'not-started' },
      in_progress:  { label: 'In progress', accentColor: '#1565c0', statusClass: 'in-progress' },
      complete:     { label: 'Complete',    accentColor: '#2e7d32', statusClass: 'complete'    },
      cancelled:    { label: 'Cancelled',   accentColor: '#b71c1c', statusClass: 'cancelled'   },
    };

    const sortedInspections = [...facilityInspections].filter(i => inspectionFilter === 'all' || (inspectionFilter === 'complete' ? i.status === 'complete' : isOpenAction(i))).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return (
      <div className="inspections-content">
        <div className="inspections-tab-header">
          <h3>Inspections</h3>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => id && navigate(`/facilities/${id}/inspection`)}
          >
            <Plus size={18} />
            Run inspection
          </button>
        </div>
        <div className="risk-source-toggle" aria-label="Filter inspections">{[['all','All'],['progress','In progress'],['complete','Complete']].map(([value,label]) => <button key={value} className={`btn btn-sm ${inspectionFilter===value?'btn-primary':'btn-secondary'}`} aria-pressed={inspectionFilter===value} onClick={()=>setInspectionFilter(value)}>{label}</button>)}</div>
        {sortedInspections.length === 0 ? (
          <div className="placeholder-content">
            <ClipboardList size={48} />
            <h4>No inspections yet</h4>
            <p>Track and schedule facility inspections, manage checkpoints, and monitor compliance.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => id && navigate(`/facilities/${id}/inspection`)}
            >
              <Plus size={18} />
              Run inspection
            </button>
          </div>
        ) : (
          <div className="inspection-list">
            {sortedInspections.map((insp) => {
              const counts = inspectionTaskCounts[insp.id] ?? { total: 0, complete: 0 };
              const progress = counts.total > 0 ? Math.round((counts.complete / counts.total) * 100) : 0;
              const meta = STATUS_META[insp.status] ?? STATUS_META.not_started;
              const typeLabel = friendlyLabel(insp.type || 'routine');
              return (
                <div
                  key={insp.id}
                  className="inspection-row"
                  style={{ '--status-color': meta.accentColor } as React.CSSProperties}
                  onClick={() => navigate(`/inspections/${insp.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/inspections/${insp.id}`)}
                >
                  <div className="inspection-row__accent" />
                  <div className="inspection-row__body">
                    <div className="inspection-row__top">
                      <span className="inspection-row__type">{typeLabel}</span>
                      <span>{inspectors.find(u=>u.id===insp.created_by_id)?.name || "Inspector not recorded"}</span>
                      <span className="inspection-row__date">{formatShortDate(insp.created_at)}</span>
                      <span className={`inspection-row__status inspection-row__status--${meta.statusClass}`}>
                        {meta.label}
                      </span>
                    </div>
                    {insp.summary && (
                      <p className="inspection-row__summary">{insp.summary}</p>
                    )}
                    <div className="inspection-row__progress">
                      <div className="inspection-progress-bar">
                        <div
                          className="inspection-progress-fill"
                          style={{ width: `${progress}%`, background: meta.accentColor }}
                        />
                      </div>
                      <span className="inspection-progress-label">
                        {`${counts.complete}/${plural(counts.total, "action")} complete`}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderLocationsTab = () => id ? <FacilityLocations
    facilityId={id} locations={locations} legacyAssets={locationAssets} onChanged={refreshAssets}
  /> : null;

  const renderReportsTab = () => id ? <Suspense fallback={<p role="status">Loading reports…</p>}><FacilityReports facilityId={id} /></Suspense> : null;

  if (loading) {
    return (
      <div className="facility-detail">{dialog}
        <div className="facility-header">
          <div className="header-navigation">
            <button className="btn btn-ghost" onClick={() => client ? navigate(`/clients/${client.id}`) : navigate(-1)}>
              <ArrowLeft size={20} />
              {client ? `Back to ${client.name}` : 'Back'}
            </button>
          </div>
        </div>
        <div className="description-section">
          <h3>Loading facility...</h3>
        </div>
      </div>
    );
  }

  if (error || !facility) {
    return (
      <div className="facility-detail">{dialog}
        <div className="facility-header">
          <div className="header-navigation">
            <button className="btn btn-ghost" onClick={() => client ? navigate(`/clients/${client.id}`) : navigate(-1)}>
              <ArrowLeft size={20} />
              {client ? `Back to ${client.name}` : 'Back'}
            </button>
          </div>
        </div>
        <div className="description-section">
          <h3>Failed to load facility</h3>
          <p>{error || 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="facility-detail">{dialog}
      <div className="facility-header">
        <div className="header-navigation">
          <button className="btn btn-ghost" onClick={() => client ? navigate(`/clients/${client.id}`) : navigate(-1)}>
            <ArrowLeft size={20} />
            {client ? `Back to ${client.name}` : 'Back'}
          </button>
        </div>

        <div className="facility-title">
          <div className="title-content">
            <h1>{facility.name}</h1>
            <div className="facility-meta">
              {(() => {
                return <><CurrentRiskBadge risk={facility.risk_summary?.current_risk ?? (facilityAggregatedRisk ? {code:cellCode(facilityAggregatedRisk.likelihood,facilityAggregatedRisk.significance),level:facilityAggregatedRisk.level} : null)} />{facility.risk_summary?.inherent_risk && <small style={{display:'block',color:'#64748b'}}>Inherent: {facility.risk_summary.inherent_risk.code} ({Array.from(new Set(assets.filter(a=>cellCode(a.risk_likelihood || 0,a.risk_significance || 0) === facility.risk_summary?.inherent_risk?.code).map(a=>a.type_name || a.type.replace(/_/g,' ')))).join(', ')})</small>}</>;
              })()}
              <span className={getStatusBadge('active').class}>{getStatusBadge('active').label}</span>
            </div>
          </div>
          <div className="facility-actions">
            <button className="btn btn-secondary" onClick={() => setShowAddTaskModal(true)}>
              <Plus size={18} />
              Add Task
            </button>
            {(currentUser?.role === 'consultant' || currentUser?.role === 'h2okay_admin') && <button className="btn btn-secondary" onClick={() => setShowSettingsModal(true)}>
              <Settings size={18} />
              Edit facility
            </button>}
            <button className="btn btn-primary" onClick={() => navigate(`/facilities/${id}/inspection`)}>
              <ClipboardList size={18} />
              Run inspection
            </button>
          </div>
        </div>
      </div>

      <div className="facility-info-bar">
        <div className="facility-info-bar__meta">
          {facility.address && (
            <span className="facility-info-bar__item">
              <MapPin size={13} />
              {facility.address}
            </span>
          )}
          {manager && (
            <span className="facility-info-bar__item">
              <span className="facility-info-bar__avatar">
                {manager.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
              {manager.name}
            </span>
          )}
        </div>
        <div className="facility-stat-chips" aria-label="Facility summary">
          {stats.map((stat, i) => (
            <button key={i} className="facility-stat-card" title={stat.label === 'Checks due' ? 'Routine checks from the risk library that are overdue or not yet done — view checks' : stat.tab === 'inspections' ? 'View inspections in progress' : 'View assets'} onClick={() => { setActiveTab(stat.tab); if(stat.tab === 'inspections') setInspectionFilter('progress'); }}>
              <span className="facility-stat-card__value">{stat.value}</span>
              <span>{stat.label}</span>
            </button>
          ))}
      <div data-tour="facility-risk"><FacilityRiskProfile incidentNote={labRisk ? `Open ${labRisk.likelihood === 5 ? 'critical' : 'high'} lab incident — risk shown as ${riskLabel(facilityAggregatedRisk!.level)}` : undefined} assets={assets} tasks={riskTasks} userId={currentUser?.id} risk={facilityAggregatedRisk} incidentCount={incidentCount} onOpenAsset={openAssetDetailsModal} /></div>
        </div>
      </div>
      {riskError && <p role="alert">Open action risks could not be loaded. Risk profile shows assets only.</p>}

      <div className="facility-tabs">
        <div className="tab-list">
          <button className={`tab ${activeTab === 'risk' ? 'active' : ''}`} onClick={() => setActiveTab('risk')}>Hazards &amp; checks</button>
          <button data-tour="facility-assets" className={`tab ${activeTab === 'assets' ? 'active' : ''}`} onClick={() => setActiveTab('assets')}>
            <Package size={18} />
            Asset Registry
          </button>
          <button data-tour="facility-locations" className={`tab ${activeTab === 'locations' ? 'active' : ''}`} onClick={() => setActiveTab('locations')}>
            <MapPin size={18} />
            Locations
          </button>
          <button className={`tab ${activeTab === 'inspections' ? 'active' : ''}`} onClick={() => setActiveTab('inspections')}>
            <ClipboardList size={18} />
            Inspections
          </button>
          <button className={`tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
            <TrendingUp size={18} />
            Reports
          </button>
          <button data-tour="facility-lab" className={`tab ${activeTab === 'lab' ? 'active' : ''}`} onClick={() => setActiveTab('lab')}>
            <FlaskConical size={18} />
            Lab
          </button>
          <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>Documents</button>
          <button className={`tab ${activeTab === 'wrmp' ? 'active' : ''}`} onClick={() => setActiveTab('wrmp')}>
            <ShieldCheck size={18} />
            Risk Management Plan
          </button>
        </div>

        <div className="tab-content">
          {completedInspection && <div role="status" className="success-message">Inspection completed · {plural(completedInspection.actions, 'action')} raised · <Link className="btn btn-secondary btn-sm" to={`/inspections/${completedInspection.id}`}>View report</Link></div>}
          {activeTab === 'risk' && <FacilityRiskAssessment facilityId={facility.id} onOpenAsset={(id,checkId)=>{const asset=assets.find(a=>a.id===id);if(asset){setSelectedCheckId(checkId);openAssetDetailsModal(asset);}}}/>}
          {activeTab === 'assets' && renderAssetsTab()}
          {activeTab === 'locations' && renderLocationsTab()}
          {activeTab === 'inspections' && renderInspectionsTab()}
          {activeTab === 'reports' && renderReportsTab()}
          {activeTab === 'lab' && <Suspense fallback={<p role="status">Loading lab…</p>}><FacilityLab facilityId={facility.id} incidentsData={labIncidents}/></Suspense>}
          {activeTab === 'documents' && facility && <FacilityDocuments facilityId={facility.id} canEdit onOpenPlan={() => setActiveTab('wrmp')} />}
          {activeTab === 'wrmp' && facility && <Suspense fallback={<p role="status">Loading plan…</p>}><WrmpPanel facilityId={facility.id} /></Suspense>}
        </div>
      </div>

      {spotlightMode && (
        <QuickAddSpotlight
          mode={spotlightMode}
          facilityId={facility.id}
          parentAsset={selectedParentAsset}
          onSuccess={handleAddAsset}
          onLocationCreated={() => { void refreshAssets(); }}
          onClose={() => { setSpotlightMode(null); setSelectedParentAsset(null); }}
        />
      )}

      {assetToClone && (
        <CloneAssetModal
          isOpen={showCloneModal}
          onClose={closeCloneModal}
          assetId={assetToClone.id}
          assetName={assetToClone.name}
          onCloned={(_newAssets) => {
            refreshAssets();
          }}
        />
      )}
      {facility && (currentUser?.role === 'consultant' || currentUser?.role === 'h2okay_admin') && (
        <FacilitySettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          facility={facility}
          onDeleted={currentUser?.role === 'consultant' || currentUser?.role === 'h2okay_admin'
            ? () => navigate(`/clients/${facility.client_id}`) : undefined}
          onSave={async (settings: FacilitySettings) => {
            await facilitiesService.saveSettings(facility.id, settings);
            setFacility(await facilitiesService.getFacilityById(facility.id));
          }}
        />
      )}

      {selectedAsset && (
        <AssetDetailsModal
          isOpen={showAssetDetailsModal}
          onClose={() => {
            setShowAssetDetailsModal(false);
            setSelectedAsset(null); setSelectedCheckId(undefined);
          }}
          initialCheckId={selectedCheckId}
          asset={selectedAsset}
          onUpdated={(updatedAsset) => {
            setAssets((prev) =>
              prev.map((asset) => (asset.id === updatedAsset.id ? updatedAsset : asset)),
            );
            setSelectedAsset(updatedAsset);
            void refreshAssets();
          }}
          parentAsset={selectedAsset.parent_asset_id ? assets.find(a => a.id === selectedAsset.parent_asset_id) || null : null}
        />
      )}

      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        onSuccess={(task) => {
          console.log('Task created:', task);
          setShowAddTaskModal(false);
          void refreshAssets();
        }}
        facilityId={facility.id}
        createdById={facility.manager_id}
      />
    </div>
  );
};

export default FacilityDetail;
